import { forwardRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/sharp-solid-svg-icons";

export const ContainedButton = styled.button`
  /* Positioning */
  position: relative;
  margin: 0px;

  /* Container */
  display: block;
  padding: ${(props) => (props.size === "large" ? "11px 16px" : "9px 12px")};
  width: ${(props) => props.width ?? "fit-content"};
  min-width: ${(props) => (props.size === "large" ? "fit-content" : "49px")};
  border: none;
  border-radius: 5px;
  background-color: color-mix(
    in oklab,
    var(--button-contained-background) 100%,
    transparent
  );

  /* Text */
  font-family: "Inter";
  color: var(--button-contained-foreground);
  font-size: ${(props) => (props.size === "large" ? "16px" : "12px")};
  line-height: ${(props) => (props.size === "large" ? "18px" : "14px")};
  font-weight: 400;
  white-space: nowrap;
  text-decoration: none;

  /* Pointer Events */
  cursor: pointer;
  &:hover {
    text-decoration: none;
    background-color: color-mix(
      in oklab,
      var(--button-contained-background) 90%,
      transparent
    );
  }

  /* Focus */
  &:focus-visible {
    outline: 2px solid var(--button-contained-background, #541299);
    outline-offset: 2px;
  }

  /* States */
  & .fa-spin {
    // Override default font awesome spin speed
    animation-duration: 1s;
  }

  /* Anchor elements don't support disabled attribute like button,
    but we can still select via the presence of the attribute */
  &[disabled] {
    background-color: var(--button-disabled-background);
    color: var(--button-disabled-foreground);
    pointer-events: none;
    cursor: default;
  }

  ${(props) =>
    props.isLoading &&
    `
      background-color: var(--button-loading-background);
      pointer-events: none;
      cursor: default;
    `}
`;

export const OutlinedButton = styled.button`
  /* Positioning */
  position: relative;
  margin: 0px;

  /* Container */
  display: block;
  width: ${(props) => props.width ?? "fit-content"};
  min-width: ${(props) => (props.size === "large" ? "fit-content" : "49px")};
  padding: ${(props) => (props.size === "large" ? "10px 16px" : "9px 12px")};
  border: solid 1px var(--button-outlined-border);
  border-radius: 5px;
  background-color: var(--button-outlined-background);

  /* Text */
  font-family: "Inter";
  color: var(--button-outlined-foreground);
  font-size: ${(props) => (props.size === "large" ? "16px" : "12px")};
  line-height: ${(props) => (props.size === "large" ? "18px" : "14px")};
  font-weight: 400;
  white-space: nowrap;
  text-decoration: none;

  /* Pointer Events */
  cursor: pointer;
  &:hover {
    background-color: var(--button-outlined-background-hover);
    text-decoration: none;
  }

  /* States */
  /* Anchor elements don't support disabled attribute like button,
    but we can still select via the presence of the attribute */
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }
`;

export const TextButton = styled.button`
  /* Positioning */
  position: relative;
  margin: 0px;

  /* Container */
  width: ${(props) => props.width ?? "fit-content"};
  padding: 0px;
  background-color: transparent;
  border: none;

  /* Text */
  font-family: "Inter";
  color: var(--button-text-foreground);
  font-size: ${(props) => (props.size === "large" ? "16px" : "12px")};
  line-height: ${(props) => (props.size === "large" ? "18px" : "14px")};
  font-weight: 400;
  white-space: nowrap;
  text-decoration: none;

  /* Pointer Events */
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }

  /* States */
  &[disabled] {
    pointer-events: none;
    cursor: default;
    opacity: 0.5;
  }
`;

// Ghost: text until hovered, when a tinted background appears. With no fill or
// border, intent is carried entirely by colour — hence `$intent` (public: `color`).
export const GhostButton = styled.button`
  /* Positioning */
  position: relative;
  margin: 0px;

  /* Container */
  display: block;
  width: ${(props) => props.width ?? "fit-content"};
  padding: ${(props) => (props.size === "large" ? "10px 16px" : "9px 12px")};
  border: none;
  border-radius: 5px;
  background-color: transparent;

  /* Text */
  font-family: "Inter";
  color: var(--button-ghost-${(props) => props.$intent}-foreground);
  font-size: ${(props) => (props.size === "large" ? "16px" : "12px")};
  line-height: ${(props) => (props.size === "large" ? "18px" : "14px")};
  font-weight: 400;
  white-space: nowrap;
  text-decoration: none;

  /* Pointer Events */
  cursor: pointer;
  &:hover {
    background-color: var(
      --button-ghost-${(props) => props.$intent}-background-hover
    );
    text-decoration: none;
  }

  /* Focus */
  &:focus-visible {
    outline: 2px solid var(--button-ghost-focus-outline);
    outline-offset: 2px;
  }

  /* States */
  &[disabled] {
    color: var(--button-ghost-disabled-foreground);
    background-color: transparent;
    pointer-events: none;
    cursor: default;
  }
`;

export const IconButton = styled(TextButton)`
  border-radius: 4px;
  padding: 1px;

  svg {
    color: var(--button-icon-foreground);
  }

  /* Pointer Events */
  cursor: pointer;

  &:hover {
    background-color: var(--button-icon-background-hover);
    text-decoration: none;
  }

  /* States */
  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  }
`;

const VARIANTS = {
  contained: ContainedButton,
  outlined: OutlinedButton,
  text: TextButton,
  ghost: GhostButton,
  icon: IconButton,
};

const INTENTS = ["primary", "secondary", "destructive"];

// forwardRef: anchors, popover triggers and focus management all need the real
// element, and without it a caller's `ref` silently goes nowhere.
const Button = forwardRef((props, ref) => {
  const {
    variant = "contained",
    isLoading,
    disabled,
    disabledMessage,
    children,
    width,
    size = "large",
    // Only ghost is themed by intent today. Held out of the spread either way
    // so it never lands on the DOM as an invalid `color` attribute.
    color,
    ...rest
  } = props;

  const VariantButton = VARIANTS[variant] ?? VARIANTS.contained;
  const intent = INTENTS.includes(color) ? color : "primary";

  return (
    <VariantButton
      ref={ref}
      data-variant={variant}
      title={disabled ? disabledMessage : ""}
      width={width}
      size={size}
      $intent={intent}
      // Destructured above, so they must be passed back explicitly or every
      // button in the app silently loses its disabled and loading states.
      disabled={disabled}
      isLoading={isLoading}
      {...rest}
    >
      {isLoading && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          style={{ position: "absolute", left: -1, width: "100%" }}
        />
      )}
      <span
        style={{
          opacity: isLoading ? 0 : 1,
        }}
      >
        {children}
      </span>
    </VariantButton>
  );
});

Button.displayName = "Button";

Button.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  disabledMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  color: PropTypes.oneOf(["primary", "secondary", "destructive"]),
  size: PropTypes.oneOf(["small", "large"]),
  variant: PropTypes.oneOf(["contained", "outlined", "text", "ghost", "icon"]),
  width: PropTypes.string,
};

export default Button;
